import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useDebounce } from "@/hooks/use-debounce";
import { SearchBar } from "./subscriptions/SearchBar";
import { SubscriptionsTable } from "./subscriptions/SubscriptionsTable";
import { useSubscriptionsData } from "./subscriptions/useSubscriptionsData";
import { StatusFilter } from "./StatusFilter";
import { SimplePagination } from "@/components/ui/shared/SimplePagination";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export function SubscriptionOverview() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearch = useDebounce(searchQuery, 500);
  
  const { subscriptions, loading, totalCount } = useSubscriptionsData(
    debouncedSearch, 
    currentPage, 
    statusFilter,
    pageSize
  );
  
  // Add debug logs to check the structure of subscriptions
  console.log("Rendered subscriptions:", subscriptions);
  
  const totalPages = Math.ceil(totalCount / pageSize);

  // Reset to first page when changing page size
  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between pb-4">
        <h2 className="text-2xl font-bold">Subscription</h2>
        <div className="flex gap-4">
          <Select
            value={pageSize.toString()}
            onValueChange={handlePageSizeChange}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size} rows
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <StatusFilter value={statusFilter} onChange={setStatusFilter} />
          <SearchBar 
            value={searchQuery}
            onChange={(value) => {
              setSearchQuery(value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>
      
      <div className="space-y-4">
        <SubscriptionsTable 
          subscriptions={subscriptions}
          loading={loading}
          searchQuery={searchQuery}
        />

        {totalPages > 1 && (
          <SimplePagination
            page={currentPage}
            totalPages={totalPages}
            loading={loading}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </Card>
  );
}
